import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Receipt, Send, ChevronDown, ChevronRight, FileText, Eye, X, PieChart, Save, Link2, Undo2, Tag, UserX, Ticket } from 'lucide-react';
import { colors, API, fmt, authHeaders, origemLabel, veNegociacao, podeGerenciarCartoes } from '../utils/theme';
import {
  PageHeader, SearchBar, Select, Table, Th, Td, TrHover, Badge, ActionButtons,
  Modal, FormField, Input, TextArea, Button, LoadingSpinner,
  ConfirmDialog, EmptyState, SearchableSelect,
} from '../components/UI';
import useSort from '../hooks/useSort';
import useIsMobile from '../hooks/useIsMobile';
import DetalheXmilesModal from '../components/DetalheXmilesModal';
import PeriodoRapido, { intervaloDoPreset } from '../components/PeriodoRapido';
import ConciliarLancamentoModal from '../components/ConciliarLancamentoModal';
import ClassificarLancamentosModal from '../components/ClassificarLancamentosModal';
import { useOperacoes } from '../components/CamposClassificacao';

// Lançamentos por período, uma linha por COMPRA (02/11/2026): PDF, Open
// Finance e TechMiles da mesma compra numa linha, com as fontes acesas — o
// servidor agrupa (GET /transacoes/visao, lib/visao-lancamentos.js).
const FONTES = [
  { v: 'pdf', label: 'PDF', cor: colors.info },
  { v: 'of', label: 'Open Finance', curto: 'OF', cor: colors.primary },
  { v: 'techmiles', label: 'TechMiles', curto: 'TM', cor: colors.success },
  { v: 'manual', label: 'Manual', curto: 'Man', cor: colors.warning },
];
const SITUACAO = {
  conciliado: { label: 'Conciliado', cor: colors.success },
  sugerido: { label: 'Sugerido', cor: colors.warning },
  pendente: { label: 'Pendente', cor: colors.danger },
  sem_fatura: { label: 'Sem fatura', cor: colors.textMuted },
  pessoal: { label: 'Pessoal', cor: colors.info },
  pagamento: { label: 'Pagamento/estorno', cor: colors.textMuted },
};
const DIVERGENCIA = {
  so_of: 'Só no Open Finance — o PDF desta fatura não tem esta compra',
  so_pdf: 'Só no PDF — o Open Finance deste cartão não trouxe esta compra',
};
const podeConciliar = () => {
  try {
    const p = JSON.parse(localStorage.getItem('xm_usuario') || '{}').paginas_permitidas;
    return p === '*' || (Array.isArray(p) && p.includes('conciliacao'));
  } catch { return false; }
};

const emptyForm = {
  cartao_id: '', nome_cartao: '', data_compra: '', data_vencimento: '', descricao: '',
  valor: '', parcela: '', categoria: '', xm_categoria_id: null, tipo: '', origem: 'MANUAL', observacoes: '',
  // Manager fields
  syncManager: true, managerCardId: '', finOperId: '', expenseTypeId: '',
  numParc: '1', invoiceType: 'A_FATURAR',
};

export default function TransacoesPage() {
  const [visao, setVisao] = useState(null);
  const [erroVisao, setErroVisao] = useState('');
  const [cartoes, setCartoes] = useState([]);
  const [loading, setLoading] = useState(true);
  // Padrão: os lançamentos de hoje, pela data da compra.
  const [periodo, setPeriodo] = useState(() => ({ preset: 'hoje', ...intervaloDoPreset('hoje') }));
  const [base, setBase] = useState('compra');
  const [filtros, setFiltros] = useState({ q: '', banco_id: '', titular_id: '', cartao_id: '', fonte: '', situacao: [] });
  const [busca, setBusca] = useState('');
  const [opcoes, setOpcoes] = useState({ bancos: [], titulares: [], cartoes: [] });
  const mobile = useIsMobile();
  // Celular: os campos do formulário um embaixo do outro.
  const colunas = (desktop) => (mobile ? 'minmax(0, 1fr)' : desktop);
  const [abertos, setAbertos] = useState(() => new Set());
  const [conciliar, setConciliar] = useState(null);
  const [desfazer, setDesfazer] = useState(null);
  const [mostrando, setMostrando] = useState(300);
  const conciliavel = useMemo(() => podeConciliar(), []);
  // Classificar e conciliar em lote (04/11/2026) — gestão, como na Conciliação.
  const gestao = useMemo(() => podeConciliar() && podeGerenciarCartoes(), []);
  const operacoes = useOperacoes();
  const [selecionados, setSelecionados] = useState(() => new Set());
  const [classificar, setClassificar] = useState(null);   // { grupos, modo }
  const [confirmando, setConfirmando] = useState(false);
  const [incluirNegociacao, setIncluirNegociacao] = useState(false);
  // Cartão inativo/cancelado fica fora por padrão: são 288 dos 377, e o
  // histórico deles enterrava o que está em uso.
  const [incluirInativos, setIncluirInativos] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [detalheXmiles, setDetalheXmiles] = useState(null);
  const [rateioModal, setRateioModal] = useState(null); // { transacao } — abrir modal de rateio

  const verPdf = (cartaoId, mes) => {
    // window.open não passa pelo interceptador de fetch, então o header
    // Authorization não vai — abria e voltava {"error":"Token não fornecido"}.
    const tk = localStorage.getItem('xm_token') || '';
    if (cartaoId && mes) window.open(`${API}/faturas/pdf/${cartaoId}/${mes}?token=${encodeURIComponent(tk)}`, '_blank');
  };
  const abrirDetalheXmiles = async (transacaoId) => {
    setDetalheXmiles({ transacaoId, loading: true, data: null });
    try {
      const r = await fetch(`${API}/xmiles/detalhe-transacao/${transacaoId}`);
      const d = await r.json();
      setDetalheXmiles({ transacaoId, loading: false, data: d });
    } catch (e) {
      setDetalheXmiles({ transacaoId, loading: false, data: null, error: e.message });
    }
  };

  // Manager reference data
  const [finOpers, setFinOpers] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  // Plano de contas do TechMiles: a fonte do dropdown de categoria do
  // lançamento LOCAL. O `expenseTypes` acima é do Manager e continua sendo
  // usado só no bloco de sync com ele (lá o id é dele).
  const [planoContas, setPlanoContas] = useState([]);
  const [managerCards, setManagerCards] = useState([]);
  const [managerLoaded, setManagerLoaded] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setFiltros(f => (f.q === busca ? f : { ...f, q: busca })), 350);
    return () => clearTimeout(t);
  }, [busca]);

  const load = useCallback(() => {
    if (!periodo.de || !periodo.ate) return;
    const params = new URLSearchParams({ de: periodo.de, ate: periodo.ate, base });
    ['q', 'banco_id', 'titular_id', 'cartao_id', 'fonte'].forEach(k => { if (filtros[k]) params.set(k, filtros[k]); });
    if (filtros.situacao.length) params.set('situacao', filtros.situacao.join(','));
    if (incluirNegociacao) params.set('incluir_negociacao', 'true');
    if (incluirInativos) params.set('incluir_inativos', 'true');
    setErroVisao('');
    fetch(`${API}/transacoes/visao?${params}`, { headers: authHeaders() })
      .then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.error || `Erro ${r.status}`); return d; })
      .then(d => { setVisao(d); setMostrando(300); })
      .catch(e => setErroVisao(e.message))
      .finally(() => setLoading(false));
  }, [periodo.de, periodo.ate, base, filtros, incluirNegociacao, incluirInativos]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetch(`${API}/transacoes/visao/opcoes`, { headers: authHeaders() }).then(r => r.json())
      .then(d => { if (d && Array.isArray(d.cartoes)) setOpcoes(d); }).catch(() => {});
  }, []);

  const grupos = (visao && visao.grupos) || [];
  const totais = (visao && visao.totais) || { qtd: 0, debitos: 0, creditos: 0, saldo: 0, por_situacao: {} };
  const alternarAberto = (chave) => setAbertos(a => { const n = new Set(a); if (n.has(chave)) n.delete(chave); else n.add(chave); return n; });
  const alternarSituacao = (v) => setFiltros(f => ({
    ...f, situacao: f.situacao.includes(v) ? f.situacao.filter(x => x !== v) : [...f.situacao, v],
  }));
  const cartoesDoFiltro = opcoes.cartoes.filter(c =>
    (!filtros.banco_id || String(c.banco_id) === String(filtros.banco_id))
    && (!filtros.titular_id || String(c.titular_id) === String(filtros.titular_id))
    && (incluirInativos || c.ativo || String(c.id) === String(filtros.cartao_id)));
  // Par fatura × TechMiles pelo localizador (04/11/2026): o servidor confere
  // de novo e concilia, deixando a fatura vinculada à emissão.
  const confirmarPorLocalizador = async (lista) => {
    const pares = lista.filter(g => g.localizador && g.localizador.candidato)
      .map(g => ({ fatura_id: g.linha_banco_id, techmiles_id: g.localizador.candidato.id }));
    if (!pares.length) return;
    const difer = lista.filter(g => g.localizador && g.localizador.candidato && !g.localizador.candidato.mesmo_valor).length;
    if (!window.confirm(`Conciliar ${pares.length} par(es) pelo localizador?`
      + (difer ? `\n\n${difer} com valor diferente entre a fatura e o TechMiles — confira antes.` : ''))) return;
    setConfirmando(true);
    try {
      const r = await fetch(`${API}/conciliacao/localizador-lote`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify({ pares }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `Erro ${r.status}`);
      alert(`${d.conciliados} conciliado(s) pelo localizador.${(d.erros || []).length ? `\n\nNão foram:\n• ${d.erros.join('\n• ')}` : ''}`);
      setSelecionados(new Set());
      load();
    } catch (e) { alert('Não foi possível conciliar: ' + e.message); }
    setConfirmando(false);
  };

  const desfazerConciliacao = async () => {
    const g = desfazer;
    setDesfazer(null);
    try {
      // Gasto pessoal se desfaz pela linha da fatura (tira a marca e a conciliação).
      const r = g.situacao === 'pessoal'
        ? await fetch(`${API}/conciliacao/desfazer-pessoal/${g.linha_banco_id}`, { method: 'POST', headers: authHeaders() })
        : await fetch(`${API}/conciliacao/${g.conciliacao_id}`, { method: 'DELETE', headers: authHeaders() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `Erro ${r.status}`);
      load();
    } catch (e) { alert('Não foi possível desfazer: ' + e.message); }
  };

  useEffect(() => {
    fetch(`${API}/cartoes`, { headers: authHeaders() }).then(r => r.json()).then(setCartoes).catch(() => {});
    // Categorias do lançamento local (18/09/2026): o plano de contas do
    // TechMiles. Era o Manager, que caiu quando as credenciais saíram do .env
    // e derrubou o dropdown em silêncio. Os dados do Manager continuam vindo
    // pelo loadManagerData(), no caminho de quem realmente sincroniza com ele.
    fetch(`${API}/plano-contas/subcategorias`, { headers: authHeaders() }).then(r => r.json())
      .then(d => setPlanoContas(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  // Load Manager reference data when sync toggle is turned on
  const loadManagerData = async () => {
    if (managerLoaded) return;
    try {
      // Ensure auth first
      await fetch(`${API}/xmiles/auth`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });

      const [opersRes, expRes, cardsRes] = await Promise.all([
        fetch(`${API}/xmiles/fin-opers`).then(r => r.json()),
        fetch(`${API}/xmiles/expense-types`).then(r => r.json()),
        fetch(`${API}/xmiles/cards-manager`).then(r => r.json()),
      ]);
      // Manager fora do ar ou sem credencial responde { error } — sem a
      // checagem a tela de Lançamentos quebrava ao abrir "Criar novo".
      setFinOpers(Array.isArray(opersRes) ? opersRes : []);
      setExpenseTypes(Array.isArray(expRes) ? expRes : []);
      setManagerCards(Array.isArray(cardsRes) ? cardsRes : []);
      setManagerLoaded(true);
    } catch (e) {
      console.error('Erro ao carregar dados do Manager:', e);
    }
  };

  const openCreate = () => { setEditId(null); setForm(emptyForm); setModalOpen(true); loadManagerData(); };
  // A visão traz um resumo de cada linha; a edição precisa dela inteira.
  const abrirEdicao = async (id) => {
    try {
      const r = await fetch(`${API}/transacoes/${id}`, { headers: authHeaders() });
      const t = await r.json();
      if (!r.ok) throw new Error(t.error || `Erro ${r.status}`);
      openEdit(t);
    } catch (e) { alert('Não foi possível abrir o lançamento: ' + e.message); }
  };
  const openEdit = (t) => {
    setEditId(t.id);
    setForm({
      cartao_id: t.cartao_id || '', nome_cartao: t.nome_cartao || '',
      data_compra: t.data_compra || '', data_vencimento: t.data_vencimento || '',
      descricao: t.descricao || '', valor: t.valor || '', parcela: t.parcela || '',
      categoria: t.categoria || '', xm_categoria_id: t.xm_categoria_id ?? null,
      tipo: t.tipo || '', origem: t.origem || 'MANUAL',
      observacoes: t.observacoes || '',
      syncManager: false, managerCardId: '', finOperId: '', expenseTypeId: '',
      numParc: '1', invoiceType: 'A_FATURAR',
    });
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.descricao.trim()) return alert('Descrição é obrigatória');
    setSaving(true);
    try {
      if (form.syncManager && !editId) {
        // Create in Manager first, which also saves locally
        if (!form.managerCardId || !form.finOperId || !form.expenseTypeId) {
          setSaving(false);
          return alert('Para sincronizar com o Manager, selecione Cartão, Operação e Categoria do Manager');
        }

        const managerPayload = {
          complement: form.descricao,
          date: form.data_compra ? new Date(form.data_compra).toISOString() : new Date().toISOString(),
          numParc: parseInt(form.numParc) || 1,
          valueFirstParc: parseFloat(form.valor) || 0,
          valueOtherParc: 0,
          dateFirstPay: form.data_vencimento ? new Date(form.data_vencimento).toISOString() : (form.data_compra ? new Date(form.data_compra).toISOString() : new Date().toISOString()),
          invoiceType: form.invoiceType || 'A_FATURAR',
          cardId: form.managerCardId,
          finOperId: form.finOperId,
          expenseTypeId: form.expenseTypeId,
          nameInCharge: '',
        };

        const res = await fetch(`${API}/xmiles/criar-lancamento`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(managerPayload),
        });
        const result = await res.json();
        if (result.error) throw new Error(result.error);

        alert(`Lançamento criado no Manager (ID: ${result.managerId}) e salvo localmente!`);
      } else {
        // Save only locally
        const method = editId ? 'PUT' : 'POST';
        const url = editId ? `${API}/transacoes/${editId}` : `${API}/transacoes`;
        const body = { ...form, valor: parseFloat(form.valor) || 0 };
        await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      }
      setModalOpen(false);
      load();
    } catch (e) { alert('Erro ao salvar: ' + e.message); }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      await fetch(`${API}/transacoes/${deleteId}`, { method: 'DELETE' });
      setDeleteId(null);
      load();
    } catch (e) { alert('Erro ao excluir: ' + e.message); }
  };

  // Group fin-opers by crDb for better UX
  const dbOpers = finOpers.filter(o => o.crDb === 'DB');
  const crOpers = finOpers.filter(o => o.crDb === 'CR');
  const expenseItems = expenseTypes.filter(e => e.typeTransaction === 'EXPENSE');
  const receiptItems = expenseTypes.filter(e => e.typeTransaction === 'RECEIPT');

  const { sorted, sortKey, sortDir, toggleSort } = useSort(grupos, 'data', 'desc');
  const sortState = { sortKey, sortDir };
  const visiveis = sorted.slice(0, mostrando);
  const escolhidos = grupos.filter(g => selecionados.has(g.chave));
  const todosMarcados = visiveis.length > 0 && visiveis.every(g => selecionados.has(g.chave));
  const marcar = (chave) => setSelecionados(s => { const n = new Set(s); if (n.has(chave)) n.delete(chave); else n.add(chave); return n; });
  const marcarTodos = () => setSelecionados(todosMarcados ? new Set() : new Set(visiveis.map(g => g.chave)));
  const comPar = escolhidos.filter(g => g.localizador && g.localizador.candidato);
  const pendentesComPar = grupos.filter(g => g.localizador && g.localizador.candidato);

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader title="Lançamentos Financeiros"
        subtitle={`${totais.qtd} compra(s) de ${periodo.de.split('-').reverse().join('/')} a ${periodo.ate.split('-').reverse().join('/')}`}
        onAdd={openCreate} addLabel="Criar novo Lançamento" />

      {/* Período e totais */}
      <div style={{
        background: colors.card, borderRadius: 12, padding: 16, marginBottom: 12,
        border: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
      }}>
        <PeriodoRapido value={periodo} onChange={setPeriodo}>
          <Select value={base} onChange={e => setBase(e.target.value)} style={{ width: 'auto', marginLeft: 8 }}
            aria-label="Data usada no período">
            <option value="compra">pela data da compra</option>
            <option value="entrada">pela data de entrada no sistema</option>
          </Select>
        </PeriodoRapido>
        <div style={{ display: 'flex', gap: 20, fontSize: 13 }}>
          <span><span style={{ color: colors.textMuted }}>Débitos:</span> <span style={{ color: colors.danger, fontWeight: 700 }}>{fmt(totais.debitos)}</span></span>
          <span><span style={{ color: colors.textMuted }}>Créditos:</span> <span style={{ color: colors.success, fontWeight: 700 }}>{fmt(totais.creditos)}</span></span>
          <span><span style={{ color: colors.textMuted }}>Saldo:</span> <span style={{ color: totais.saldo <= 0 ? colors.success : colors.danger, fontWeight: 700 }}>{fmt(totais.saldo)}</span></span>
        </div>
      </div>

      <SearchBar value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar descrição, cartão, fidelizado…">
        <div style={{ minWidth: 170 }}>
          <SearchableSelect small value={filtros.banco_id} placeholder="Todos os bancos"
            options={opcoes.bancos.map(b => ({ value: String(b.id), label: b.nome }))}
            onChange={v => setFiltros(f => ({ ...f, banco_id: v || '', cartao_id: '' }))} />
        </div>
        <div style={{ minWidth: 190 }}>
          <SearchableSelect small value={filtros.titular_id} placeholder="Todos os titulares"
            options={opcoes.titulares.map(t => ({ value: String(t.id), label: t.nome }))}
            onChange={v => setFiltros(f => ({ ...f, titular_id: v || '', cartao_id: '' }))} />
        </div>
        <div style={{ minWidth: 220 }}>
          <SearchableSelect small value={filtros.cartao_id} placeholder="Todos os cartões"
            options={cartoesDoFiltro.map(c => ({ value: String(c.id), label: `${c.nome}${c.final ? ` ·${c.final}` : ''}${c.ativo ? '' : ' (inativo)'}` }))}
            onChange={v => setFiltros(f => ({ ...f, cartao_id: v || '' }))} />
        </div>
        <Select value={filtros.fonte} onChange={e => setFiltros(f => ({ ...f, fonte: e.target.value }))} style={{ width: 'auto', minWidth: 140 }}>
          <option value="">Todas as fontes</option>
          {FONTES.map(f => <option key={f.v} value={f.v}>{f.label}</option>)}
        </Select>
        {/* Cartão em negociação só é opção pra quem enxerga esses cartões. */}
        {veNegociacao() && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: colors.textMuted, whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={incluirNegociacao} onChange={e => setIncluirNegociacao(e.target.checked)}
              style={{ cursor: 'pointer', accentColor: colors.warning }} />
            Incluir cartões em negociação
          </label>
        )}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: colors.textMuted, whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={incluirInativos} onChange={e => setIncluirInativos(e.target.checked)}
            style={{ cursor: 'pointer', accentColor: colors.warning }} />
          Incluir cartões inativos
        </label>
      </SearchBar>

      {/* Situação: filtra e mostra quantas compras há em cada uma */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {Object.entries(SITUACAO).map(([v, s]) => {
          const ativo = filtros.situacao.includes(v);
          return (
            <button key={v} type="button" onClick={() => alternarSituacao(v)} style={{
              padding: '4px 10px', borderRadius: 14, fontSize: 12, cursor: 'pointer', fontWeight: 600,
              border: `1px solid ${ativo ? s.cor : colors.border}`, background: ativo ? `${s.cor}22` : 'transparent',
              color: ativo ? s.cor : colors.textMuted,
            }}>
              {s.label} {(totais.por_situacao || {})[v] != null && !filtros.situacao.length ? `· ${totais.por_situacao[v]}` : ''}
            </button>
          );
        })}
      </div>

      {erroVisao && (
        <div style={{ padding: 12, borderRadius: 8, border: `1px solid ${colors.danger}`, color: colors.danger, marginBottom: 12, fontSize: 13 }}>
          {erroVisao}
        </div>
      )}
      {visao && visao.truncado && (
        <div style={{ padding: 10, borderRadius: 8, border: `1px solid ${colors.warning}`, color: colors.warning, marginBottom: 12, fontSize: 13 }}>
          O período tem mais de {visao.limite} lançamentos — mostrando os mais recentes. Encurte o período ou use os filtros.
        </div>
      )}

      {gestao && pendentesComPar.length > 0 && !escolhidos.length && (
        <div style={{ padding: 10, borderRadius: 8, border: `1px solid ${colors.success}55`, background: `${colors.success}10`,
          marginBottom: 12, fontSize: 13, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Ticket size={15} color={colors.success} />
          <span>{pendentesComPar.length} linha(s) da fatura com localizador que bate com um lançamento do TechMiles.</span>
          <Button size="sm" variant="outline" color={colors.success} disabled={confirmando}
            onClick={() => confirmarPorLocalizador(pendentesComPar)}>
            {confirmando ? 'Conciliando…' : 'Revisar e conciliar todos'}
          </Button>
        </div>
      )}
      {gestao && escolhidos.length > 0 && (
        <div style={{ position: 'sticky', top: 0, zIndex: 20, padding: 10, borderRadius: 8, marginBottom: 12,
          border: `1px solid ${colors.primary}`, background: colors.card, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{escolhidos.length} selecionado(s)</span>
          <Button size="sm" icon={Tag} onClick={() => setClassificar({ grupos: escolhidos, modo: 'classificar' })}>Categoria · empresa · operação</Button>
          <Button size="sm" variant="outline" icon={UserX} onClick={() => setClassificar({ grupos: escolhidos, modo: 'pessoal' })}>Gasto pessoal</Button>
          {comPar.length > 0 && (
            <Button size="sm" variant="outline" color={colors.success} icon={Ticket} disabled={confirmando}
              onClick={() => confirmarPorLocalizador(comPar)}>Conciliar {comPar.length} pelo localizador</Button>
          )}
          <Button size="sm" variant="outline" color={colors.textMuted} onClick={() => setSelecionados(new Set())}>Limpar seleção</Button>
        </div>
      )}

      {grupos.length === 0 ? (
        <EmptyState message="Nenhum lançamento no período" icon={Receipt} />
      ) : (
        <Table>
          <thead><tr>
            {gestao && (
              <Th align="center" style={{ width: 32 }}>
                <input type="checkbox" checked={todosMarcados} onChange={marcarTodos} title="Selecionar os visíveis" style={{ cursor: 'pointer' }} />
              </Th>
            )}
            <Th />
            <Th sortKey="data" sortState={sortState} onSort={toggleSort}>Data</Th>
            <Th sortKey="nome_cartao" sortState={sortState} onSort={toggleSort}>Cartão</Th>
            <Th sortKey="titular_nome" sortState={sortState} onSort={toggleSort}>Titular</Th>
            <Th sortKey="descricao" sortState={sortState} onSort={toggleSort}>Descrição</Th>
            <Th sortKey="valor" sortState={sortState} onSort={toggleSort}>Valor</Th>
            <Th>Parcela</Th>
            <Th sortKey="categoria" sortState={sortState} onSort={toggleSort}>Classificação</Th>
            {FONTES.map(f => <Th key={f.v} align="center">{f.curto || f.label}</Th>)}
            <Th sortKey="situacao" sortState={sortState} onSort={toggleSort}>Situação</Th>
            <Th align="center">Ações</Th>
          </tr></thead>
          <tbody>
            {visiveis.map(g => {
              const aberto = abertos.has(g.chave);
              const sit = SITUACAO[g.situacao] || { label: g.situacao, cor: colors.textMuted };
              const membros = [...g.fontes.pdf, ...g.fontes.of, ...g.fontes.techmiles, ...g.fontes.manual];
              return (
                <React.Fragment key={g.chave}>
                  <TrHover style={selecionados.has(g.chave) ? { background: `${colors.primary}12` } : undefined}>
                    {gestao && (
                      <Td style={{ textAlign: 'center' }}>
                        <input type="checkbox" checked={selecionados.has(g.chave)} onChange={() => marcar(g.chave)} style={{ cursor: 'pointer' }} />
                      </Td>
                    )}
                    <Td>
                      <button onClick={() => alternarAberto(g.chave)} title="Ver as linhas de cada fonte"
                        style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', padding: 2 }}>
                        {aberto ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </button>
                    </Td>
                    <Td style={{ whiteSpace: 'nowrap' }}>{g.data ? g.data.split('-').reverse().join('/') : '-'}</Td>
                    <Td style={{ fontWeight: 500 }}>{g.nome_cartao || '-'}</Td>
                    <Td>{g.titular_nome || '-'}</Td>
                    <Td style={{ maxWidth: 260 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={g.descricao}>{g.descricao}</div>
                      {/* Chamado #71: o fidelizado diz de quem é o gasto nos clubes. */}
                      {g.fidelizado && (
                        <div style={{ fontSize: 10, marginTop: 2, color: colors.info, fontWeight: 600,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title="Fidelizado — titular da conta de fidelidade deste lançamento">
                          👤 {g.fidelizado}
                        </div>
                      )}
                      {/* Localizador na descrição (04/11/2026): com o lançamento do
                          TechMiles do mesmo localizador, o par está pronto pra confirmar. */}
                      {g.localizador && (
                        <div style={{ fontSize: 10, marginTop: 2, fontWeight: 600,
                          color: g.localizador.candidato ? colors.success : colors.textMuted }}
                          title={g.localizador.candidato
                            ? `TechMiles: ${g.localizador.candidato.descricao} — ${fmt(g.localizador.candidato.valor)}${g.localizador.candidato.mesmo_valor ? '' : ' (valor diferente)'}${g.localizador.candidatos > 1 ? ` · ${g.localizador.candidatos} lançamentos com este localizador` : ''}`
                            : 'Nenhum lançamento do TechMiles deste cartão com este localizador'}>
                          🎫 {g.localizador.codigo}
                          {g.localizador.candidato ? ` ↔ TechMiles ${fmt(g.localizador.candidato.valor)}${g.localizador.candidato.mesmo_valor ? '' : ' ≠'}` : ' · sem par no TechMiles'}
                        </div>
                      )}
                      {g.vinculo && g.vinculo.tipo === 'EMISSAO' && g.vinculo.emissao_ref && !g.localizador && (
                        <div style={{ fontSize: 10, marginTop: 2, color: colors.info }} title="Vinculada à emissão">🎫 {g.vinculo.emissao_ref}</div>
                      )}
                    </Td>
                    <Td style={{ fontWeight: 600, color: g.valor < 0 ? colors.danger : colors.success, whiteSpace: 'nowrap' }}>{fmt(g.valor)}</Td>
                    <Td>{g.parcela || '-'}</Td>
                    <Td style={{ maxWidth: 220 }}>
                      {(() => {
                        const centro = g.centro || g.centro_regra;
                        const conteudo = (
                          <>
                            <div style={{ fontSize: 12, color: g.categoria ? colors.text : colors.textDim,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {g.categoria ? `${g.categoria}${g.subcategoria ? ` / ${g.subcategoria}` : ''}` : (gestao ? '+ classificar' : '—')}
                            </div>
                            {(g.empresa || centro) && (
                              <div style={{ fontSize: 10, marginTop: 2, color: colors.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {g.empresa && <span style={{ color: colors.info, fontWeight: 600 }}>{g.empresa}</span>}
                                {g.empresa && centro && ' · '}
                                {centro && <span style={{ fontWeight: g.centro ? 700 : 400 }}
                                  title={g.centro ? 'Operação escolhida à mão' : 'Operação pela regra automática'}>
                                  {operacoes.rotulo(centro)}{g.centro ? '' : ' (auto)'}
                                </span>}
                              </div>
                            )}
                          </>
                        );
                        return gestao ? (
                          <button type="button" onClick={() => setClassificar({ grupos: [g], modo: 'classificar' })}
                            title="Classificar: categoria, empresa e operação"
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', maxWidth: '100%', display: 'block' }}>
                            {conteudo}
                          </button>
                        ) : conteudo;
                      })()}
                    </Td>
                    {FONTES.map(f => {
                      const linhas = g.fontes[f.v];
                      const sugerido = f.v === 'techmiles' && !linhas.length && g.sugestoes && g.sugestoes.length > 0;
                      return (
                        <Td key={f.v} style={{ textAlign: 'center' }}>
                          <span title={linhas.length ? linhas.map(l => `${l.descricao} — ${fmt(l.valor)}`).join('\n')
                            : sugerido ? 'Sugestão do motor: falta confirmar' : `Não está no ${f.label}`}
                            style={{
                              display: 'inline-block', width: 12, height: 12, borderRadius: '50%',
                              background: linhas.length ? f.cor : 'transparent',
                              border: `2px ${sugerido ? 'dashed' : 'solid'} ${linhas.length || sugerido ? f.cor : colors.border}`,
                            }} />
                        </Td>
                      );
                    })}
                    <Td style={{ whiteSpace: 'nowrap' }}>
                      <Badge text={sit.label} color={sit.cor} />
                      {g.divergencia && (
                        <span title={DIVERGENCIA[g.divergencia]} style={{ marginLeft: 6, color: colors.warning, fontSize: 11, fontWeight: 700 }}>
                          ⚠ {g.divergencia === 'so_of' ? 'só OF' : 'só PDF'}
                        </span>
                      )}
                    </Td>
                    <Td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {gestao && g.localizador && g.localizador.candidato && (
                        <Button size="sm" color={colors.success} icon={Ticket} disabled={confirmando}
                          onClick={() => confirmarPorLocalizador([g])}
                          title={`Conciliar com "${g.localizador.candidato.descricao}" (mesmo localizador)`}>Confirmar par</Button>
                      )}
                      {gestao && ['pendente', 'sugerido'].includes(g.situacao) && g.linha_banco_id && (
                        <button type="button" onClick={() => setClassificar({ grupos: [g], modo: 'pessoal' })} title="Gasto pessoal"
                          style={{ marginLeft: 4, background: `${colors.info}18`, border: `1px solid ${colors.info}44`, borderRadius: 6,
                            color: colors.info, cursor: 'pointer', padding: '4px 6px', verticalAlign: 'middle' }}>
                          <UserX size={14} />
                        </button>
                      )}
                      {conciliavel && ['pendente', 'sugerido', 'sem_fatura'].includes(g.situacao) && (g.linha_banco_id || g.linha_operacao_id) && (
                        <Button size="sm" variant="outline" icon={Link2} onClick={() => setConciliar(g)}>
                          {g.situacao === 'sugerido' ? 'Revisar' : 'Conciliar'}
                        </Button>
                      )}
                      {conciliavel && ((g.situacao === 'conciliado' && g.conciliacao_id) || (g.situacao === 'pessoal' && g.linha_banco_id)) && (
                        <Button size="sm" variant="outline" color={colors.textMuted} icon={Undo2} onClick={() => setDesfazer(g)}>Desfazer</Button>
                      )}
                    </Td>
                  </TrHover>
                  {aberto && membros.map(l => (
                    <tr key={`${g.chave}-${l.id}`} style={{ background: `${colors.bg}` }}>
                      {gestao && <Td />}
                      <Td />
                      <Td style={{ whiteSpace: 'nowrap', fontSize: 12, color: colors.textMuted }}>{l.data ? l.data.split('-').reverse().join('/') : '-'}</Td>
                      <Td colSpan={2} style={{ fontSize: 12 }}>
                        {(() => {
                          const f = FONTES.find(x => x.v === ({ FATURA_PDF: 'pdf', FATURA: 'pdf', OPEN_FINANCE: 'of', MANUAL: 'manual' }[l.origem] || 'techmiles'));
                          return <Badge text={f.v === 'techmiles' ? origemLabel(l.origem) : f.label} color={f.cor} />;
                        })()}
                        {l.conciliado && <span style={{ marginLeft: 6, color: colors.success, fontSize: 11 }}>conciliada</span>}
                        {l.of_removida && <span style={{ marginLeft: 6, color: colors.warning, fontSize: 11 }}>removida na Pluggy</span>}
                      </Td>
                      <Td style={{ fontSize: 12 }}>{l.descricao}</Td>
                      <Td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmt(l.valor)}</Td>
                      <Td style={{ fontSize: 12 }}>{l.parcela || '-'}</Td>
                      <Td colSpan={FONTES.length + 2} style={{ fontSize: 12, color: colors.textMuted }}>
                        {l.categoria ? `${l.categoria}${l.subcategoria ? ` / ${l.subcategoria}` : ''}` : ''}
                        {l.empresa && <span style={{ marginLeft: 6, color: colors.info }}>{l.empresa}</span>}
                      </Td>
                      <Td style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        {l.cartao_id && (l.origem === 'FATURA_PDF' || l.origem === 'FATURA') && (
                          <button onClick={() => verPdf(l.cartao_id, l.mes_referencia)} title="Ver fatura PDF"
                            style={{ background: `${colors.info}22`, border: `1px solid ${colors.info}44`, borderRadius: 6, color: colors.info, cursor: 'pointer', padding: '4px 6px' }}>
                            <FileText size={14} />
                          </button>
                        )}
                        {(l.origem === 'XMILES' || l.origem === 'XMILES_PARC') && (
                          <button onClick={() => abrirDetalheXmiles(l.id)} title="Detalhes XMiles"
                            style={{ background: `${colors.warning}22`, border: `1px solid ${colors.warning}44`, borderRadius: 6, color: colors.warning, cursor: 'pointer', padding: '4px 6px' }}>
                            <Eye size={14} />
                          </button>
                        )}
                        <button onClick={() => setRateioModal({ ...l, nome_cartao: g.nome_cartao, titular_nome: g.titular_nome })} title="Distribuir por centro de custo"
                          style={{ background: `${colors.success}22`, border: `1px solid ${colors.success}44`, borderRadius: 6, color: colors.success, cursor: 'pointer', padding: '4px 6px' }}>
                          <PieChart size={14} />
                        </button>
                        <ActionButtons onEdit={() => abrirEdicao(l.id)} onDelete={() => setDeleteId(l.id)} />
                      </Td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      )}
      {sorted.length > mostrando && (
        <div style={{ textAlign: 'center', margin: '12px 0' }}>
          <Button variant="outline" onClick={() => setMostrando(m => m + 300)}>
            Mostrar mais ({sorted.length - mostrando} restantes)
          </Button>
        </div>
      )}

      {conciliar && (
        <ConciliarLancamentoModal grupo={conciliar} onClose={() => setConciliar(null)}
          onFeito={() => { setConciliar(null); load(); }} />
      )}
      <ConfirmDialog isOpen={!!desfazer} onClose={() => setDesfazer(null)} onConfirm={desfazerConciliacao}
        title={desfazer && desfazer.situacao === 'pessoal' ? 'Desfazer gasto pessoal' : 'Desfazer conciliação'}
        message={desfazer ? `Desfazer ${desfazer.situacao === 'pessoal' ? 'o gasto pessoal' : 'a conciliação'} de "${desfazer.descricao}"? A linha volta a ficar pendente.` : ''} />
      {classificar && (
        <ClassificarLancamentosModal grupos={classificar.grupos} modoInicial={classificar.modo} operacoes={operacoes}
          onClose={() => setClassificar(null)}
          onFeito={() => { setSelecionados(new Set()); load(); }} />
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}
        title={editId ? 'Editar Lançamento' : 'Criar novo Lançamento'} width={700}>

        {/* Sync Toggle - only for new records */}
        {!editId && (
          <div style={{
            background: form.syncManager ? `${colors.primary}15` : `${colors.bg}`,
            border: `1px solid ${form.syncManager ? colors.primary : colors.border}`,
            borderRadius: 10, padding: 14, marginBottom: 20,
            display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
            transition: 'all 0.2s',
          }} onClick={() => {
            const newVal = !form.syncManager;
            setForm(f => ({ ...f, syncManager: newVal }));
            if (newVal) loadManagerData();
          }}>
            <div style={{
              width: 40, height: 22, borderRadius: 11,
              background: form.syncManager ? colors.primary : colors.border,
              position: 'relative', transition: 'background 0.2s',
            }}>
              <div style={{
                width: 18, height: 18, borderRadius: '50%', background: '#fff',
                position: 'absolute', top: 2,
                left: form.syncManager ? 20 : 2,
                transition: 'left 0.2s',
              }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: form.syncManager ? colors.primary : colors.text }}>
                <Send size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
                Criar também no XMiles Manager
              </div>
              <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
                {form.syncManager
                  ? 'O lançamento será criado no Manager e sincronizado automaticamente.'
                  : 'Salvar apenas localmente. Ative para enviar ao Manager.'}
              </div>
            </div>
          </div>
        )}

        {/* Basic Fields */}
        <FormField label="Descrição" required>
          <Input value={form.descricao} onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))} placeholder="Descrição do lançamento" />
        </FormField>

        <div style={{ display: 'grid', gridTemplateColumns: colunas('1fr 1fr'), gap: mobile ? 0 : 16 }}>
          <FormField label="Data da Compra">
            <Input type="date" value={form.data_compra} onChange={e => setForm(f => ({ ...f, data_compra: e.target.value }))} />
          </FormField>
          <FormField label="Data de Vencimento">
            <Input type="date" value={form.data_vencimento} onChange={e => setForm(f => ({ ...f, data_vencimento: e.target.value }))} />
          </FormField>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: colunas('1fr 1fr 1fr'), gap: mobile ? 0 : 16 }}>
          <FormField label="Valor" required>
            <Input type="number" step="0.01" value={form.valor} onChange={e => setForm(f => ({ ...f, valor: e.target.value }))} placeholder="0.00" />
          </FormField>
          <FormField label="Parcelas">
            <Input type="number" min="1" value={form.numParc || '1'} onChange={e => setForm(f => ({ ...f, numParc: e.target.value, parcela: parseInt(e.target.value) > 1 ? `1/${e.target.value}` : '' }))} />
          </FormField>
          <FormField label="Tipo Faturamento">
            <Select value={form.invoiceType || 'A_FATURAR'} onChange={e => setForm(f => ({ ...f, invoiceType: e.target.value }))}>
              <option value="FATURADO">FATURADO</option>
              <option value="A_FATURAR">A FATURAR</option>
            </Select>
          </FormField>
        </div>

        {/* Manager-specific fields */}
        {form.syncManager && !editId && (
          <div style={{
            background: `${colors.primary}08`, border: `1px solid ${colors.primary}22`,
            borderRadius: 10, padding: 16, marginTop: 8,
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: colors.primary, marginBottom: 12 }}>
              Campos do XMiles Manager
            </div>

            <FormField label="Cartão do Manager" required>
              <SearchableSelect
                value={form.managerCardId}
                onChange={val => setForm(f => ({ ...f, managerCardId: val }))}
                placeholder="Selecione o cartão..."
                options={managerCards.map(c => ({
                  value: c.id, label: `${c.name} - ${c.holder} (${c.typeCard}) Final ${c.finalNum}`,
                }))}
              />
            </FormField>

            <div style={{ display: 'grid', gridTemplateColumns: colunas('1fr 1fr'), gap: mobile ? 0 : 16 }}>
              <FormField label="Operação Financeira" required>
                <SearchableSelect
                  value={form.finOperId}
                  onChange={val => setForm(f => ({ ...f, finOperId: val }))}
                  placeholder="Selecione..."
                  options={[
                    ...dbOpers.map(o => ({ value: o.id, label: o.name, group: 'Débito' })),
                    ...crOpers.map(o => ({ value: o.id, label: o.name, group: 'Crédito' })),
                  ]}
                />
              </FormField>

              <FormField label="Categoria Despesa/Receita" required>
                <SearchableSelect
                  value={form.expenseTypeId}
                  onChange={val => setForm(f => ({ ...f, expenseTypeId: val }))}
                  placeholder="Selecione..."
                  options={[
                    ...expenseItems.map(e => ({ value: e.id, label: e.expenseName, group: 'Despesa' })),
                    ...receiptItems.map(e => ({ value: e.id, label: e.expenseName, group: 'Receita' })),
                  ]}
                />
              </FormField>
            </div>
          </div>
        )}

        {/* Local-only fields (when not syncing to Manager) */}
        {(!form.syncManager || editId) && (
          <div style={{ display: 'grid', gridTemplateColumns: colunas('1fr 1fr'), gap: mobile ? 0 : 16, marginTop: 8 }}>
            <FormField label="Cartão Local">
              <SearchableSelect
                value={form.cartao_id}
                onChange={val => {
                  const c = cartoes.find(c => c.id === parseInt(val));
                  setForm(f => ({ ...f, cartao_id: val, nome_cartao: c ? c.nome_cartao : f.nome_cartao }));
                }}
                placeholder="Selecione..."
                options={cartoes.map(c => ({ value: String(c.id), label: `${c.nome_cartao} - ${c.titular_nome}` }))}
              />
            </FormField>
            <FormField label="Categoria Despesa/Receita">
              <SearchableSelect
                // Vinculado ao plano? `categoria` guarda o GRUPO — o que se
                // escolhe aqui é a subcategoria, e é ela que casa com as opções.
                value={form.xm_categoria_id ? (form.subcategoria || form.categoria) : form.categoria}
                onChange={val => {
                  // O id do plano sobe junto: sem ele o texto não classifica
                  // linha de fatura, e um vínculo antigo sobreviveria à troca.
                  const doPlano = planoContas.find(s => s.nome === val);
                  setForm(f => ({ ...f, categoria: val,
                    xm_categoria_id: doPlano ? doPlano.xm_categoria_id : null }));
                }}
                placeholder="Selecione..."
                options={[
                  ...planoContas.map(s => ({ value: s.nome, label: s.nome, group: s.grupo })),
                  // Fallback do Manager, quando ele responde: só o que o plano não tem.
                  ...expenseItems.filter(e => !planoContas.some(s => s.nome === e.expenseName))
                    .map(e => ({ value: e.expenseName, label: e.expenseName, group: 'Despesa' })),
                  ...receiptItems.filter(e => !planoContas.some(s => s.nome === e.expenseName))
                    .map(e => ({ value: e.expenseName, label: e.expenseName, group: 'Receita' })),
                ]}
              />
            </FormField>
          </div>
        )}

        <FormField label="Observações">
          <TextArea value={form.observacoes} onChange={e => setForm(f => ({ ...f, observacoes: e.target.value }))} placeholder="Observações..." />
        </FormField>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          <Button variant="outline" color={colors.textMuted} onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button
            onClick={save} disabled={saving}
            color={form.syncManager && !editId ? colors.success : colors.primary}
            icon={form.syncManager && !editId ? Send : undefined}
          >
            {saving ? 'Salvando...'
              : editId ? 'Salvar Alterações'
              : form.syncManager ? 'Criar no Manager + Local'
              : 'Criar Lançamento'}
          </Button>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete}
        title="Excluir Lançamento" message="Tem certeza que deseja excluir este lançamento?"
      />

      {detalheXmiles && (
        <DetalheXmilesModal
          detalhe={detalheXmiles}
          onClose={() => setDetalheXmiles(null)}
        />
      )}

      {rateioModal && (
        <RateioModal
          transacao={rateioModal}
          onClose={() => setRateioModal(null)}
          onSaved={() => { setRateioModal(null); load(); }}
        />
      )}
    </div>
  );
}

// =================== MODAL DE RATEIO ===================
function RateioModal({ transacao, onClose, onSaved }) {
  const [centros, setCentros] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [categoriaId, setCategoriaId] = useState(null);
  const [rateio, setRateio] = useState([]);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState('');

  const inputStyle = {
    padding: '7px 10px', borderRadius: 7, border: `1px solid ${colors.border}`,
    background: colors.bg, color: colors.text, fontSize: 13, outline: 'none', width: 85, textAlign: 'right',
  };

  // Carregar centros, categorias e rateio existente
  useEffect(() => {
    Promise.all([
      fetch(`${API}/centros-custo`, { headers: authHeaders() }).then(r => r.json()),
      fetch(`${API}/centros-custo/categorias`, { headers: authHeaders() }).then(r => r.json()),
      fetch(`${API}/centros-custo/rateio/${transacao.id}`, { headers: authHeaders() }).then(r => r.json()),
    ]).then(([cs, cats, existente]) => {
      setCentros(cs.filter(c => c.ativo));
      setCategorias(cats);
      if (existente.length > 0) {
        // Tem rateio existente: carregar valores
        setCategoriaId(existente[0].categoria_id || null);
        const mapa = {};
        existente.forEach(e => { mapa[e.centro_custo_id] = parseFloat(e.percentual); });
        setRateio(cs.filter(c => c.ativo).map(c => ({ ...c, percentual: mapa[c.id] || 0 })));
      } else {
        // Sem rateio: carregar template padrão (global)
        fetch(`${API}/centros-custo/templates?categoria_id=null`, { headers: authHeaders() })
          .then(r => r.json()).then(tmpl => {
            const mapa = {};
            tmpl.forEach(t => { mapa[t.centro_custo_id] = parseFloat(t.percentual); });
            setRateio(cs.filter(c => c.ativo).map(c => ({ ...c, percentual: mapa[c.id] || 0 })));
          });
      }
    }).catch(console.error);
  }, [transacao.id]);

  // Quando muda categoria, carregar template dela
  const onChangeCategoria = async (catId) => {
    setCategoriaId(catId === '' ? null : parseInt(catId));
    const param = catId === '' ? 'null' : catId;
    const r = await fetch(`${API}/centros-custo/templates?categoria_id=${param}`, { headers: authHeaders() });
    const tmpl = await r.json();
    const mapa = {};
    tmpl.forEach(t => { mapa[t.centro_custo_id] = parseFloat(t.percentual); });
    setRateio(prev => prev.map(c => ({ ...c, percentual: mapa[c.id] || 0 })));
  };

  const updatePct = (id, val) => {
    setRateio(prev => prev.map(c => c.id === id ? { ...c, percentual: parseFloat(val) || 0 } : c));
  };

  const totalPct = rateio.reduce((s, r) => s + (r.percentual || 0), 0);
  const valorTotal = Math.abs(transacao.valor || 0);

  const salvar = async () => {
    if (Math.abs(totalPct - 100) > 0.1) { setErro(`Percentuais devem somar 100% (atual: ${totalPct.toFixed(1)}%)`); return; }
    setErro('');
    setSaving(true);
    const r = await fetch(`${API}/centros-custo/rateio/${transacao.id}`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({
        categoria_id: categoriaId,
        rateios: rateio.map(c => ({ centro_custo_id: c.id, percentual: c.percentual })),
        valor_total: valorTotal,
      }),
    });
    setSaving(false);
    if (!r.ok) { const d = await r.json(); setErro(d.error || 'Erro ao salvar'); return; }
    onSaved();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div style={{ width: 'min(520px, calc(100vw - 16px))', maxHeight: '94vh', overflowY: 'auto', background: colors.card, borderRadius: 16, border: `1px solid ${colors.border}`, padding: 'clamp(16px, 5vw, 28px)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: colors.text, display: 'flex', alignItems: 'center', gap: 8 }}>
              <PieChart size={18} color={colors.success} /> Distribuir por Centro de Custo
            </h2>
            <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>
              {transacao.descricao} · <b style={{ color: colors.text }}>{fmt(valorTotal)}</b>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: colors.textDim, cursor: 'pointer' }}><X size={18} /></button>
        </div>

        {/* Categoria */}
        <div style={{ marginBottom: 18 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: colors.textMuted, marginBottom: 4 }}>
            Categoria (opcional — carrega template)
          </label>
          <select value={categoriaId === null ? '' : categoriaId} onChange={e => onChangeCategoria(e.target.value)}
            style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bg, color: colors.text, fontSize: 13 }}>
            <option value="">⭐ Padrão Global</option>
            {categorias.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
        </div>

        {/* Rateio por centro */}
        {rateio.map(c => (
          <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: c.cor, flexShrink: 0 }} />
            <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: colors.text }}>{c.nome}</div>
            <input type="number" min={0} max={100} step={0.1} value={c.percentual === 0 ? '' : c.percentual}
              onChange={e => updatePct(c.id, e.target.value)} placeholder="0" style={inputStyle} />
            <span style={{ fontSize: 12, color: colors.textMuted, width: 30 }}>%</span>
            <span style={{ fontSize: 12, color: colors.textDim, width: 90, textAlign: 'right' }}>
              {c.percentual > 0 ? fmt(valorTotal * c.percentual / 100) : '—'}
            </span>
          </div>
        ))}

        {/* Total */}
        <div style={{
          marginTop: 12, padding: '9px 14px', borderRadius: 8,
          background: Math.abs(totalPct - 100) <= 0.1 ? `${colors.success}15` : `${colors.warning}15`,
          border: `1px solid ${Math.abs(totalPct - 100) <= 0.1 ? colors.success : colors.warning}30`,
          display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 600,
          color: Math.abs(totalPct - 100) <= 0.1 ? colors.success : colors.warning,
        }}>
          <span>Total: {totalPct.toFixed(1)}%{Math.abs(totalPct - 100) <= 0.1 ? ' ✓' : ` (falta ${(100 - totalPct).toFixed(1)}%)`}</span>
          <span>{fmt(rateio.reduce((s, c) => s + (valorTotal * (c.percentual || 0) / 100), 0))}</span>
        </div>

        {erro && <div style={{ marginTop: 8, color: colors.danger, fontSize: 12 }}>{erro}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
          <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 8, border: `1px solid ${colors.border}`, background: 'transparent', color: colors.textMuted, fontSize: 13, cursor: 'pointer' }}>Cancelar</button>
          <button onClick={salvar} disabled={saving || Math.abs(totalPct - 100) > 0.1} style={{
            padding: '9px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
            background: Math.abs(totalPct - 100) <= 0.1 ? colors.success : colors.textDim,
            color: '#fff', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <Save size={14} />{saving ? 'Salvando...' : 'Salvar Rateio'}
          </button>
        </div>
      </div>
    </div>
  );
}
